import { useTranslation } from 'react-i18next';
import { MyAdsBulkPromotionServicePicker } from './MyAdsBulkPromotionServicePicker';
import { MyAdsBulkPromotionPaymentDrawer } from './MyAdsBulkPromotionPaymentDrawer';
import type { useMyAdsBulkPromotionPayment } from '@/features/Profile/sections/MyAds/hooks';
import { Button } from '@/components/UI/Button/Button';
import { WarningModal } from '@/components/Ad/WarningModal';
import { InfoDrawer } from '@/components/Ad/Promotions/InfoDrawer';
import { AddBalance } from '@/components/Payment/AddBalance';

type Props = {
  selectedCount: number;
  payment: ReturnType<typeof useMyAdsBulkPromotionPayment>;
};

export function MyAdsBulkPromotionFlow({ selectedCount, payment }: Props) {
  const { t } = useTranslation();

  return (
    <>
      <MyAdsBulkPromotionServicePicker
        selectedCount={selectedCount}
        services={payment.services}
        isOpen={payment.isServicePickerOpen}
        isLoading={payment.isServiceOptionsLoading}
        onClose={payment.closeServicePicker}
        onSelect={payment.selectService}
      />

      <MyAdsBulkPromotionPaymentDrawer
        selectedCount={selectedCount}
        selectedService={payment.selectedService}
        selectedOption={payment.selectedOption}
        selectedOptionId={payment.selectedOptionId}
        walletBalance={payment.user.walletBalance}
        isPaymentLoading={payment.isPaymentLoading}
        onClose={payment.closePayment}
        onSelectOption={payment.selectOption}
        onPay={payment.pay}
      />

      <WarningModal
        isOpen={payment.isNotEnoughFundsOpen}
        onClose={payment.toggleNotEnoughFunds}
        title={t('common.attention')}
        description={t('pages.ad.not_enough_money_top_up_your_balance')}
        actions={
          <>
            <Button
              appearance="secondaryOutlined"
              isFullWidth
              borderRadius={14}
              onClick={payment.toggleNotEnoughFunds}
            >
              {t('common.close')}
            </Button>
            <Button
              appearance="secondary"
              isFullWidth
              borderRadius={14}
              onClick={payment.startAddBalance}
            >
              {t('common.top_up')}
            </Button>
          </>
        }
      />

      <InfoDrawer
        isOpen={Boolean(payment.paymentError)}
        onClose={payment.closePaymentError}
        description={payment.paymentError}
        isError
      />

      <AddBalance
        isOpen={payment.isAddBalanceOpen}
        toggle={payment.toggleAddBalance}
        isAppleGooglePayEnabled={payment.user.isAppleGooglePayEnabled}
        onSuccess={() => payment.toggleBalanceTopUpSuccess(true)}
        paymentMethods={payment.user.paymentMethods}
      />

      <WarningModal
        isOpen={payment.isBalanceTopUpSuccessOpen}
        onClose={() => payment.toggleBalanceTopUpSuccess(false)}
        icon="check-circle-rounded.svg"
        iconSize={30}
        title={t('pages.cabinet.add_balance_success_message')}
        description=""
      />
    </>
  );
}
